import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import EntHero from '../../components/enterprise/EntHero';
import EntStatCard from '../../components/enterprise/EntStatCard';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EnterpriseContactDrawer from '../../components/enterprise/EnterpriseContactDrawer';

const SOURCE_LABELS = {
  nfc: 'NFC tap',
  manual: 'Manual',
  invitation: 'Invitation',
};

const SORT_OPTIONS = [
  { value: 'dateAddedLatest:desc', label: 'Newest first' },
  { value: 'dateAddedFirst:asc', label: 'Oldest first' },
  { value: 'name:asc', label: 'Contact name A–Z' },
  { value: 'company:asc', label: 'Company A–Z' },
  { value: 'savedByCount:desc', label: 'Most saved by' },
];

const DEFAULT_FILTERS = {
  search: '',
  memberId: '',
  teamId: '',
  source: '',
  hasLocation: '',
  dateFrom: '',
  dateTo: '',
  missingCompany: false,
  sort: 'dateAddedLatest:desc',
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default function EnterpriseContactsPage() {
  const { enterpriseId } = useParams();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  // Debounce free-text search into filters
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Filter dropdown data (existing admin endpoints)
  useEffect(() => {
    if (!enterpriseId) return;
    let cancelled = false;
    api.get('/api/enterprise/members', { params: { enterpriseId } })
      .then((res) => { if (!cancelled) setMembers(res.data.members || []); })
      .catch(() => {});
    api.get('/api/enterprise/teams', { params: { enterpriseId } })
      .then((res) => { if (!cancelled) setTeams(res.data.teams || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enterpriseId]);

  // Main data fetch
  useEffect(() => {
    if (!enterpriseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const [sortBy, sortOrder] = filters.sort.split(':');
    const params = {
      enterpriseId,
      page,
      limit,
      sortBy,
      sortOrder,
    };
    if (filters.search) params.search = filters.search;
    if (filters.memberId) params.memberId = filters.memberId;
    if (filters.teamId) params.teamId = filters.teamId;
    if (filters.source) params.source = filters.source;
    if (filters.hasLocation) params.hasLocation = filters.hasLocation;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.missingCompany) params.missingCompany = 'true';

    api.get('/api/enterprise/contacts', { params })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load company contacts');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enterpriseId, filters, page, limit]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setPage(1);
  };

  const recentActive = filters.dateFrom === isoDaysAgo(7) && !filters.dateTo;
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search || filters.memberId || filters.teamId || filters.source ||
      filters.hasLocation || filters.dateFrom || filters.dateTo || filters.missingCompany ||
      filters.sort !== DEFAULT_FILTERS.sort
    );
  }, [filters]);

  const contacts = data?.contacts || [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  const hero = (
    <EntHero
      title="Company Contacts"
      lead="A read-only CRM view of every contact saved by your active team members — who they met, who saved them, and when. Personal notes are never shared."
    />
  );

  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      {/* Summary cards */}
      {summary && (
        <div className="ent-stat-row">
          <EntStatCard label="Unique contacts" value={summary.uniqueContacts} accent />
          <EntStatCard label="Saved records" value={summary.totalContacts} />
          <EntStatCard label="Added this month" value={summary.contactsThisMonth} />
          <EntStatCard
            label="Top contributor"
            value={summary.topContributor ? summary.topContributor.name : '—'}
            hint={summary.topContributor ? `${summary.topContributor.count} contacts` : 'No activity yet'}
            valueClassName="ent-card-value-sm"
          />
        </div>
      )}

      {/* Toolbar: search, filters, quick filters */}
      <div className="ent-crm-toolbar">
        <div className="ent-crm-search">
          <input
            type="search"
            className="ent-crm-input"
            placeholder="Search name, email, company, title, or team member…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search contacts"
          />
        </div>
        <div className="ent-crm-filters">
          <select
            className="ent-crm-select"
            value={filters.memberId}
            onChange={(e) => setFilter('memberId', e.target.value)}
            aria-label="Filter by team member"
          >
            <option value="">All members</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>{m.name}</option>
            ))}
          </select>
          {teams.length > 0 && (
            <select
              className="ent-crm-select"
              value={filters.teamId}
              onChange={(e) => setFilter('teamId', e.target.value)}
              aria-label="Filter by team"
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
          )}
          <select
            className="ent-crm-select"
            value={filters.source}
            onChange={(e) => setFilter('source', e.target.value)}
            aria-label="Filter by source"
          >
            <option value="">All sources</option>
            <option value="nfc">NFC tap</option>
            <option value="manual">Manual</option>
            <option value="invitation">Invitation</option>
          </select>
          <select
            className="ent-crm-select"
            value={filters.hasLocation}
            onChange={(e) => setFilter('hasLocation', e.target.value)}
            aria-label="Filter by location"
          >
            <option value="">Any location</option>
            <option value="true">Location available</option>
            <option value="false">No location</option>
          </select>
          <input
            type="date"
            className="ent-crm-input ent-crm-date"
            value={filters.dateFrom}
            onChange={(e) => setFilter('dateFrom', e.target.value)}
            aria-label="Added from date"
          />
          <input
            type="date"
            className="ent-crm-input ent-crm-date"
            value={filters.dateTo}
            onChange={(e) => setFilter('dateTo', e.target.value)}
            aria-label="Added to date"
          />
          <select
            className="ent-crm-select"
            value={filters.sort}
            onChange={(e) => setFilter('sort', e.target.value)}
            aria-label="Sort contacts"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="ent-crm-quick">
          <button
            type="button"
            className={`ent-crm-chip ${recentActive ? 'active' : ''}`}
            onClick={() => {
              if (recentActive) {
                setFilters((f) => ({ ...f, dateFrom: '', dateTo: '' }));
              } else {
                setFilters((f) => ({ ...f, dateFrom: isoDaysAgo(7), dateTo: '' }));
              }
              setPage(1);
            }}
          >
            Added last 7 days
          </button>
          <button
            type="button"
            className={`ent-crm-chip ${filters.missingCompany ? 'active' : ''}`}
            onClick={() => setFilter('missingCompany', !filters.missingCompany)}
          >
            Missing company
          </button>
          {hasActiveFilters && (
            <button type="button" className="ent-crm-chip ent-crm-chip-clear" onClick={clearFilters}>
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <EntSkeleton variant="rows" count={8} />
      ) : contacts.length === 0 ? (
        <EntEmptyState
          title={hasActiveFilters ? 'No contacts match these filters' : 'No company contacts yet'}
          lead={
            hasActiveFilters
              ? 'Try adjusting or clearing the filters above.'
              : 'Contacts will appear here once your accepted team members start saving contacts with their Velox cards.'
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="ent-table-wrap ent-crm-table-wrap">
            <table className="ent-table ent-crm-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Role / title</th>
                  <th>Saved by</th>
                  <th>Source</th>
                  <th>First added</th>
                  <th>Latest</th>
                  <th>Location</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr
                    key={c.contactKey}
                    className="ent-row-clickable"
                    onClick={() => setSelected(c)}
                  >
                    <td>
                      <div className="ent-crm-contact-cell">
                        <span className="ent-crm-contact-name">{c.name || '—'}</span>
                        <span className="ent-crm-contact-email">{c.email || ''}</span>
                      </div>
                    </td>
                    <td>{c.company || '—'}</td>
                    <td>{[c.role, c.title].filter(Boolean).join(' · ') || '—'}</td>
                    <td>
                      <div className="ent-crm-savedby">
                        <span className="ent-crm-savedby-name" title={(c.savedBy || []).map((s) => s.name).join(', ')}>
                          {c.savedBy?.[0]?.name || '—'}
                        </span>
                        {c.savedByCount > 1 && (
                          <span className="ent-crm-group-chip" title={`Saved by ${c.savedByCount} team members`}>
                            ×{c.savedByCount}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {(c.sources || []).map((s) => (
                        <span className="ent-crm-source-chip" key={s}>{SOURCE_LABELS[s] || s}</span>
                      ))}
                    </td>
                    <td>{formatDate(c.dateAddedFirst)}</td>
                    <td>{formatDate(c.dateAddedLatest)}</td>
                    <td>
                      {c.hasLocation ? (
                        <span className="ent-loc ent-loc-yes">
                          <span className="ent-loc-dot" aria-hidden="true" /> Available
                        </span>
                      ) : (
                        <span className="ent-loc ent-loc-no">None</span>
                      )}
                    </td>
                    <td className="ent-actions-cell">
                      <button type="button" className="ent-btn-analytics" onClick={() => setSelected(c)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="ent-crm-cards">
            {contacts.map((c) => (
              <button
                type="button"
                key={c.contactKey}
                className="ent-crm-card"
                onClick={() => setSelected(c)}
              >
                <div className="ent-crm-card-top">
                  <span className="ent-crm-contact-name">{c.name || '—'}</span>
                  {c.savedByCount > 1 && (
                    <span className="ent-crm-group-chip">×{c.savedByCount}</span>
                  )}
                </div>
                <div className="ent-crm-card-sub">
                  {[c.role, c.company].filter(Boolean).join(' · ') || c.email || '—'}
                </div>
                <div className="ent-crm-card-meta">
                  <span>Saved by {c.savedBy?.[0]?.name || '—'}{c.savedByCount > 1 ? ` +${c.savedByCount - 1}` : ''}</span>
                  <span>{formatDate(c.dateAddedLatest)}</span>
                  {c.hasLocation ? (
                    <span className="ent-loc ent-loc-yes">
                      <span className="ent-loc-dot" aria-hidden="true" /> Location
                    </span>
                  ) : null}
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="ent-crm-pagination">
              <button
                type="button"
                className="ent-crm-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </button>
              <span className="ent-crm-page-info">
                Page {pagination.page} of {pagination.totalPages}
                <span className="ent-crm-page-total"> · {pagination.total} contacts</span>
              </span>
              <button
                type="button"
                className="ent-crm-page-btn"
                disabled={!pagination.hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <EnterpriseContactDrawer contact={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
